import { Component, Input, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import {
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil, tap } from 'rxjs/operators';

import { weightUnits } from '../../../invoice/config/shipstation.config';
import { SalesOrderConfig } from '../../../sales-order/configs/sales-order.config';
import { RfqApiService } from '../../../shared';
import { OptionInterface } from '../../../shared/interfaces/common.interface';
import { IFieldListItem } from '../../../shared/interfaces/condition-field.interface';
import { IRuleCustomFieldValue } from '../../../shared/interfaces/custom-field.interface';
import {
    EComparisonOperators,
    EComparisonOperatorsDisplayText,
} from '../../../shared/interfaces/operator.interface';
import { TagApiService } from '../../../shared/services/api/tag-api.service';
import {
    trackByLabel,
    trackById,
    trackByControlValue,
} from '../../../shared/util/track-by.util';
import { AutomationRuleConfig } from '../../config/automation-rule.config';
import {
    EAutomationRuleConditions,
    EAutomationRuleLogicalOperators,
    EAutomationRuleEvents,
    IAutomationRuleConditionConfig,
    AutomationEventType,
} from '../../interfaces';

@Component({
  selector: 'lum-automation-rule-condition-form',
  templateUrl: './automation-rule-condition-form.component.html',
  styleUrls: ['./automation-rule-condition-form.component.scss'],
})
export class AutomationRuleConditionFormComponent implements OnInit, OnDestroy {
  @Input()
  form :UntypedFormGroup;

  @Input()
  customFields :IRuleCustomFieldValue[];

  @Input()
  ruleIds :{ id :number; name :string }[];

  trackById = trackById;
  trackByControlValue = trackByControlValue;
  trackByLabel = trackByLabel;

  formEventType: AutomationEventType = '';
  filteredConditions: IAutomationRuleConditionConfig[] = [];

  automationRuleConfig = AutomationRuleConfig;
  eAutomationRuleConditions = EAutomationRuleConditions;
  logicalOperators = EAutomationRuleLogicalOperators;
  comparisonOperators = EComparisonOperators;
  comparisonOperatorsDisplay = EComparisonOperatorsDisplayText;

  fieldList :IFieldListItem[] = [];
  customFieldList :IFieldListItem[] = [];

  weightUnits :OptionInterface[] = weightUnits;

  orderStatuses = SalesOrderConfig.orderStatus;

  conditionFieldList :IFieldListItem[];
  hasDateFieldList :IFieldListItem[];
  isTrueFalseFieldList :IFieldListItem[];
  hasTagFieldList :IFieldListItem[];

  tags$ :Observable<OptionInterface[]>;
  tags :OptionInterface[];
  filteredRfqs :OptionInterface[] = [];
  private searchUpdated$ = new Subject<string>();
  private onDestroy$ = new Subject();
  rfqs$ :Observable<any>;

  constructor(
    private fb :UntypedFormBuilder,
    private tagApiService :TagApiService,
    private rfqApiService :RfqApiService,
  ) {}

  ngOnInit() :void {
    // Initialize filteredConditions with all conditions by default
    this.filteredConditions = this.automationRuleConfig.conditions;

    // Only call setFormEventType if form exists and has event control
    if (this.form && this.form.get('event')) {
      this.setFormEventType();

      // Subscribe to event changes
      this.form
        .get('event')
        .valueChanges.pipe(takeUntil(this.onDestroy$))
        .subscribe(() => {
          this.setFormEventType();
        });
    }

    this.tagApiService.getTags({});
    this.buildFieldList(false);
    this.getFromService();
    this.setFieldDropdown();
    this.getRfqsFromObservable().subscribe();
    this.onSearchUpdate().subscribe();
  }

  ngOnChanges(changes :SimpleChanges) :void {
    if (changes['customFields'].currentValue) {
      this.customFieldList = [];
      this.buildFieldList(true);
    }
  }

  ngOnDestroy() {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onSelectCondition(condition :string, index :number) :void {
    const conditionFormGroup = <UntypedFormGroup>this.conditionsFormArray.at(index); // get the first FormGroup
    const controlNames = Object.keys(conditionFormGroup.controls);

    for (const controlName of controlNames) {
      if (controlName !== 'logicalOperator' && controlName !== 'condition') {
        conditionFormGroup.removeControl(controlName);
      }
    }

    // Add new control based on condition
    switch (condition) {
      case EAutomationRuleConditions.BOOLEAN:
        conditionFormGroup.addControl(
          'boolean',
          this.fb.group({
            type: [
              this.getConditionType(this.eAutomationRuleConditions.BOOLEAN),
              Validators.required,
            ],
            field: ['', Validators.required],
            value: ['', Validators.required],
          }),
        );
        this.setFieldDropdownByCondition('BOOLEAN');
        break;
      case EAutomationRuleConditions.ITEM_COUNT:
        conditionFormGroup.addControl(
          'itemCount',
          this.fb.group({
            type: [
              this.getConditionType(this.eAutomationRuleConditions.ITEM_COUNT),
              Validators.required,
            ],
            minCount: ['', Validators.required],
          }),
        );
        break;
      case EAutomationRuleConditions.TEXT:
        conditionFormGroup.addControl(
          'text',
          this.fb.group({
            type: [
              this.getConditionType(this.eAutomationRuleConditions.TEXT),
              Validators.required,
            ],
            field: ['', Validators.required],
            value: ['', Validators.required],
            operator: ['', Validators.required],
          }),
        );
        break;
      case EAutomationRuleConditions.DATE:
        conditionFormGroup.addControl(
          'date',
          this.fb.group({
            type: [
              this.getConditionType(this.eAutomationRuleConditions.DATE),
              Validators.required,
            ],
            field: ['', Validators.required],
            value: this.fb.group({
              start: ['', Validators.required],
              end: [''],
            }),
            operator: ['', Validators.required],
          }),
        );
        this.setFieldDropdownByCondition('DATE');
        break;
      case EAutomationRuleConditions.AGGREGATE_RELATIONSHIP:
        conditionFormGroup.addControl(
          'aggregateRelationship',
          this.fb.group({
            type: [
              this.getConditionType(
                this.eAutomationRuleConditions.AGGREGATE_RELATIONSHIP,
              ),
              Validators.required,
            ],
            relation: ['', Validators.required],
            aggregationType: ['', Validators.required],
            attribute: ['', Validators.required],
          }),
        );
        break;
      case EAutomationRuleConditions.HAS_NOT_PERFORMED_ACTION:
        conditionFormGroup.addControl(
          'hasNotPerformedAction',
          this.fb.group({
            type: [
              this.getConditionType(
                this.eAutomationRuleConditions.HAS_NOT_PERFORMED_ACTION,
              ),
              Validators.required,
            ],
            ruleId: [null], // null is now valid for self-reference
          }),
        );
        break;
      case EAutomationRuleConditions.HAS_TAG:
        conditionFormGroup.addControl(
          'hasTag',
          this.fb.group({
            type: [
              this.getConditionType(this.eAutomationRuleConditions.HAS_TAG),
              Validators.required,
            ],
            relationPath: ['', Validators.required],
            tag: ['', Validators.required],
          }),
        );
        this.setFieldDropdownByCondition('HAS_TAG');
        break;
      case EAutomationRuleConditions.ORDER_AGE:
        conditionFormGroup.addControl(
          'orderAge',
          this.fb.group({
            type: [
              this.getConditionType(this.eAutomationRuleConditions.ORDER_AGE),
              Validators.required,
            ],
            value: ['', Validators.required],
            operator: ['', Validators.required],
          }),
        );
        break;
      case EAutomationRuleConditions.ORDER_STATUS:
        conditionFormGroup.addControl(
          'orderStatus',
          this.fb.group({
            type: [
              this.getConditionType(EAutomationRuleConditions.ORDER_STATUS),
              Validators.required,
            ], // Type of condition
            value: ['', Validators.required],
            operator: ['', Validators.required],
            selected: [[]],
          }),
        );
        break;
      case EAutomationRuleConditions.SHIPPING_METHOD:
        conditionFormGroup.addControl(
          'shippingMethod',
          this.fb.group({
            type: [
              this.getConditionType(EAutomationRuleConditions.SHIPPING_METHOD),
              Validators.required,
            ],
            value: ['', Validators.required],
            operator: ['', Validators.required],
          }),
        );
        break;
      case EAutomationRuleConditions.PRODUCT_SKU:
        conditionFormGroup.addControl(
          'productSku',
          this.fb.group({
            type: [
              this.getConditionType(EAutomationRuleConditions.PRODUCT_SKU),
              Validators.required,
            ],
            value: ['', Validators.required],
            operator: ['', Validators.required],
            selected: [[], Validators.required],
          }),
        );
        break;
      case EAutomationRuleConditions.ORDER_TOTAL:
        conditionFormGroup.addControl(
          'orderTotal',
          this.fb.group({
            type: [
              this.getConditionType(EAutomationRuleConditions.ORDER_TOTAL),
              Validators.required,
            ],
            value: ['', Validators.required],
            operator: ['', Validators.required],
          }),
        );
        break;
      case EAutomationRuleConditions.ORDER_WEIGHT:
        conditionFormGroup.addControl(
          'orderWeight',
          this.fb.group({
            type: [
              this.getConditionType(EAutomationRuleConditions.ORDER_WEIGHT),
              Validators.required,
            ],
            value: this.fb.group({
              units: ['', Validators.required],
              value: ['', Validators.required],
            }),
            operator: ['', Validators.required],
          }),
        );
        break;
      case EAutomationRuleConditions.APPROVAL_STATUS:
        conditionFormGroup.addControl(
          'approvalStatus',
          this.fb.group({
            type: [
              this.getConditionType(EAutomationRuleConditions.APPROVAL_STATUS),
              Validators.required,
            ],
            value: ['', Validators.required],
            operator: ['', Validators.required],
          }),
        );
        break;
      case EAutomationRuleConditions.BOM_ITEM_RECEIVED:
        conditionFormGroup.addControl(
          'bomItemReceived',
          this.fb.group({
            type: [
              this.getConditionType(this.eAutomationRuleConditions.BOM_ITEM_RECEIVED),
              Validators.required,
            ],
            value: this.fb.group({}),
          }),
        );
        break;
    }
  }

  onAddConditionRow(logicalOperator ?:EAutomationRuleLogicalOperators) :void {
    // If it's receiving event type, only allow adding conditions if there are less than 1 condition
    if (this.formEventType === 'receiving') {
      const existingConditions = this.conditionsFormArray.controls;
      const hasItemReceived = existingConditions.some(
        (control) =>
          control.get('condition').value ===
          EAutomationRuleConditions.BOM_ITEM_RECEIVED,
      );

      // If condition is already added, don't allow adding more
      if (hasItemReceived) {
        return;
      }
    }

    this.conditionsFormArray.push(
      this.fb.group({
        condition: ['', Validators.required],
        logicalOperator: [logicalOperator || ''],
      }),
    );
  }

  onRemoveCondition(index :number) :void {
    this.conditionsFormArray.removeAt(index);

    // If condtionsFormArray length is 1, remove logicalOperator
    if (this.conditionsFormArray.length === 1) {
      this.conditionsFormArray.at(0).patchValue({ logicalOperator: '' });
    }
  }

  onLogicalOperatorChange(index :number, logicalOperator :'and' | 'or') :void {
    const condition = this.conditionsFormArray.at(index);
    condition.patchValue({ logicalOperator });
  }

  onSelectTag(event :any, index :number) :void {
    const tags :any[] = [];

    event.tag.forEach((tag :number) => {
      tags.push(tag);
    });
  }

  /**
   * Sets the form event type and filters available conditions based on the selected event.
   * For receiving events, only BOM-related conditions are shown.
   * For other events (order, invoice), BOM-related conditions are hidden.
   */
  setFormEventType() :void {
    // Reset if form or event control is not available
    if (!this.form?.get('event')) {
      this.#resetToDefaultState();
      return;
    }

    const eventValue = this.form.get('event').value;

    // Reset if no event is selected
    if (!eventValue) {
      this.#resetToDefaultState();
      return;
    }

    // Clear conditions when switching between different event types
    if (this.formEventType !== '' && this.formEventType !== eventValue) {
      this.#clearConditions();
    }

    // Set form event type and filter conditions
    this.formEventType = this.#determineEventType(eventValue);
    this.#filterConditionsForEventType(this.formEventType);
  }

  /**
   * Determines the event type based on the event value
   */
  #determineEventType(eventValue: string) :AutomationEventType {
    if (
      [
        EAutomationRuleEvents.ORDER_CREATED,
        EAutomationRuleEvents.ORDER_UPDATED,
      ].some((event) => eventValue === event)
    ) {
      return 'order';
    }
    if (
      [
        EAutomationRuleEvents.INVOICE_CREATED,
        EAutomationRuleEvents.INVOICE_UPDATED,
      ].some((event) => eventValue === event)
    ) {
      return 'invoice';
    }
    if (eventValue === EAutomationRuleEvents.RECEIVING) {
      return EAutomationRuleEvents.RECEIVING;
    }
    return '' as AutomationEventType;
  }

  /**
   * Filters conditions based on the event type
   */
  #filterConditionsForEventType(eventType :AutomationEventType) :void {
    const isBomCondition = (condition: any) =>
      condition.id === EAutomationRuleConditions.BOM_ITEM_RECEIVED;

    this.filteredConditions = this.automationRuleConfig.conditions.filter(
      eventType === EAutomationRuleEvents.RECEIVING
        ? isBomCondition
        : (condition) => !isBomCondition(condition),
    );
  }

  /**
   * Resets the form to its default state
   */
  #resetToDefaultState() :void {
    this.formEventType = '' as AutomationEventType;
    this.#filterConditionsForEventType('' as AutomationEventType);
  }

  /**
   * Clears all existing conditions from the form
   */
  #clearConditions() :void {
    while (this.conditionsFormArray.length !== 0) {
      this.conditionsFormArray.removeAt(0);
    }
  }

  setFieldDropdown() :void {
    switch (this.formEventType) {
      case 'invoice':
        this.hasDateFieldList = this.automationRuleConfig.hasDateField
          .filter((field) => field.eventType === 'invoice')
          .flatMap((field) => field.values);

        this.isTrueFalseFieldList = this.automationRuleConfig.isTrueFalseField
          .filter((field) => field.eventType === 'invoice')
          .flatMap((field) => field.values);

        this.hasTagFieldList = this.automationRuleConfig.hasTagRelationPath
          .filter((field) => field.eventType === 'invoice')
          .flatMap((field) => field.values);
        break;
      case 'order':
        this.hasDateFieldList = this.automationRuleConfig.hasDateField
          .filter((field) => field.eventType === 'order')
          .flatMap((field) => field.values);

        this.isTrueFalseFieldList = this.automationRuleConfig.isTrueFalseField
          .filter((field) => field.eventType === 'order')
          .flatMap((field) => field.values);

        this.hasTagFieldList = this.automationRuleConfig.hasTagRelationPath
          .filter((field) => field.eventType === 'order')
          .flatMap((field) => field.values);
        break;
      default:
        this.hasDateFieldList = [];
        this.hasTagFieldList = [];
        this.isTrueFalseFieldList = [];
        break;
    }
  }

  setFieldDropdownByCondition(condition :string) :void {
    switch (condition) {
      case 'BOOLEAN':
        this.isTrueFalseFieldList = this.automationRuleConfig.isTrueFalseField
          .filter((field) => field.eventType === this.formEventType)
          .flatMap((field) => field.values);
        break;
      case 'DATE':
        this.hasDateFieldList = this.automationRuleConfig.hasDateField
          .filter((field) => field.eventType === this.formEventType)
          .flatMap((field) => field.values);
        break;
      case 'HAS_TAG':
        this.hasTagFieldList = this.automationRuleConfig.hasTagRelationPath
          .filter((field) => field.eventType === this.formEventType)
          .flatMap((field) => field.values);
        break;
      default:
        this.conditionFieldList = [];
        break;
    }
  }

  buildFieldList(customLoaded :boolean) :void {
    if (!customLoaded) {
      this.fieldList = this.automationRuleConfig.conditionFields;
    } else {
      this.fieldList = [];
      this.customFields.forEach((custom) => {
        const customField = {
          id: `custom_field_${custom.label.toLowerCase()}`,
          name: custom.label,
          type: `custom_field_${custom.label.toLowerCase()}`,
        };
        this.customFieldList.push(customField);
      });

      this.fieldList = [
        ...this.automationRuleConfig.conditionFields,
        ...this.customFieldList,
      ];
    }

    // TODO: Check on backend to see if we need the relation
    // Update aggregateRelationship to what is needed for the condition
    // for(let i = 0; i < this.form.controls['conditions'].value.length; i++) {
    //   if (this.form.controls['conditions'].value[i].action === 'aggregateRelationship') {
    //     this.setAttributeRelation(this.form.controls['conditions'].value[i].aggregateRelationship['relation'], false)
    //   }
    // }
  }

  getConditionType(conditionType :string) :string {
    let type :string = '';

    this.automationRuleConfig.conditions.map((condition) => {
      if (condition.id === conditionType) {
        type = condition.type;
      }
    });

    return type;
  }

  onAddProduct(option :OptionInterface, index :number) :void {
    const conditionFormGroup = <UntypedFormGroup>this.conditionsFormArray.at(index);
    const rfqControl = conditionFormGroup.get('productSku');
    const selectedControl = rfqControl.get('selected');
    selectedControl.setValue([...selectedControl.value, option]);

    const valueControl = rfqControl.get('value');
    const selectedValues = selectedControl.value.map(
      (data :OptionInterface) => data.value,
    );
    valueControl.setValue(selectedValues.join(','));
  }

  onRemoveProduct(option :OptionInterface, index :number) :void {
    const conditionFormGroup = <UntypedFormGroup>this.conditionsFormArray.at(index);
    const rfqControl = conditionFormGroup.get('productSku');
    const selectedControl = rfqControl.get('selected');
    const filteredValues = selectedControl.value.filter(
      (data :OptionInterface) => data.value !== option.value,
    );
    selectedControl.setValue(filteredValues);

    const valueControl = rfqControl.get('value');
    const selectedValues = selectedControl.value.map(
      (data :OptionInterface) => data.value,
    );
    valueControl.setValue(selectedValues.join(','));
  }

  onAddStatus(option :OptionInterface, index :number) :void {
    const conditionFormGroup = <UntypedFormGroup>this.conditionsFormArray.at(index);
    const statusControl = conditionFormGroup.get('orderStatus');
    const selectedControl = statusControl.get('selected');
    selectedControl.setValue([...selectedControl.value, option]);

    const valueControl = statusControl.get('value');
    const selectedValues = selectedControl.value.map(
      (data :OptionInterface) => data.value,
    );
    valueControl.setValue(selectedValues.join(','));
  }

  onRemoveStatus(option :OptionInterface, index :number) :void {
    const conditionFormGroup = <UntypedFormGroup>this.conditionsFormArray.at(index);
    const statusControl = conditionFormGroup.get('orderStatus');
    const selectedControl = statusControl.get('selected');
    const filteredValues = selectedControl.value.filter(
      (data :OptionInterface) => data.value !== option.value,
    );
    selectedControl.setValue(filteredValues);

    const valueControl = statusControl.get('value');
    const selectedValues = selectedControl.value.map(
      (data :OptionInterface) => data.value,
    );
    valueControl.setValue(selectedValues.join(','));
  }

  onSearchKeywordChange(event :any) :void {
    this.searchUpdated$.next(event);
  }

  private getFromService() :void {
    this.tags$ = this.tagApiService.tags$;
    this.rfqs$ = this.rfqApiService.rfqs$;
  }

  private getRfqsFromObservable() :Observable<any> {
    return this.rfqs$.pipe(
      tap((rfqs :any) => {
        this.filteredRfqs = rfqs?.map((rfq) => {
          return {
            value: rfq.sku,
            label: rfq.product_name,
          };
        });
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private onSearchUpdate() :Observable<string> {
    return this.searchUpdated$.asObservable().pipe(
      debounceTime(500),
      distinctUntilChanged(),
      tap((keyword :string) => {
        this.rfqApiService.search({
          per_page: '15',
          offset: '0',
          sku: keyword,
        });
      }),
      takeUntil(this.onDestroy$),
    );
  }

  get conditionsFormArray() :UntypedFormArray {
    return this.form.get('conditions') as UntypedFormArray;
  }

  get orderStatusOptions() :OptionInterface[] {
    return this.orderStatuses.map((status) => {
      return {
        value: status.id,
        label: status.name,
      };
    });
  }
}
